import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ISettlements } from '@/modules/Settlements/types';
import { IReconciliation } from '@/modules/workingDayReconciliation/types';
import axiosClient from '@/shared/lib/axios';

/**************************************days******************************************* *
                            Reconciliation Days
/********************************************************************************* */

export const getReconciliationDays = async ({
  entityId,
  ...restParams
}: IReconciliation.IGetDaysSchema): Promise<{ date: string }[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/workday/dates/${entityId}`,
    {
      params: restParams,
    }
  );
  return data;
};
export const useGetReconciliationDays = (
  params: IReconciliation.IGetDaysSchema
) => {
  return useQuery(
    ['getReconciliationDays', params],
    () => getReconciliationDays(params),
    {
      enabled: Boolean(
        (params.entityId === 0 ? true : params.entityId) &&
          params.startDate &&
          params.endDate
      ),
    }
  );
};

/***************************************card****************************************** *
                            Reconciliation Summary
/********************************************************************************* */
// TODO: bonuses update - recall the page when add new bonus
export const getWorkdaySummary = async ({
  entityId,
  ...restParams
}: IReconciliation.IGetWorkdaySummarySchema): Promise<
  IReconciliation.IGetWorkdaySummary[]
> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/workday/summary/${entityId}`,
    {
      params: restParams,
    }
  );
  return data;
};
export const useGetWorkdaySummary = (
  params: IReconciliation.IGetWorkdaySummarySchema
) => {
  return useQuery(
    ['getWorkdaySummary', params],
    () => getWorkdaySummary(params),
    {
      enabled: Boolean(params.entityId === 0 ? true : params.entityId),
      refetchOnMount: true,
    }
  );
};

/***************************************Modals****************************************** *
                            Reconciliation Expenses (Deductions ,bonuses) Table 
/********************************************************************************* */
// TODO: bonuses update - recall the page when add new bonus

export const getWorkdayExpensesTable = async ({
  expenseType,
  ...restParams
}: IReconciliation.IGetWorkdaySupplierDetailsSchema): Promise<
  IReconciliation.IGetWorkdayBonusesTable[]
> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/workday/${
      expenseType === 'bonus' ? 'supplierbonus' : 'supplierdeduction'
    }`,
    {
      params: restParams,
    }
  );
  return data;
};

export const useGetWorkdayExpensesTable = (
  params: IReconciliation.IGetWorkdaySupplierDetailsSchema
) => {
  return useQuery(
    [
      params.expenseType === 'deduction'
        ? 'supplierdeduction'
        : 'supplierbonus',
      params,
    ],
    () => getWorkdayExpensesTable(params),
    {
      enabled: Boolean(
        (params.entityId === 0 ? true : params.entityId) && params.supplierId
      ),
    }
  );
};

/*****************************************Modals**************************************** *
                            add edit supplier expenses (Deduction,bonus) 
/********************************************************************************* */
// MISSING ID
// DATA SHOULD BE FORM DATA
// API URL IS '/company/portalInsights/workday/supplierbonus/${params.id}' IN CASE OF EDIT
export const useAddOrEditSupplierExpenses = (
  expenseType: IReconciliation.IModalType,
  operationType: IReconciliation.IModalOperationType
) => {
  const requestMethod = operationType === 'add' ? 'post' : 'put';
  const queryClient = useQueryClient();
  return useMutation(
    (data: IReconciliation.IAddEditSupplierExpensesSchema) => {
      // console.log('is edit: ', operationType)
      const expenseId = `${
        operationType === 'edit' ? '/' + data.expenseId : ''
      }`;

      const url = `/company/portalInsights/workday/${
        expenseType === 'deduction'
          ? data.expenseType === 'Supervision'
            ? 'workerdeduction'
            : 'supplierdeduction'
          : data.expenseType === 'Supervision'
          ? 'workerbonus'
          : 'supplierbonus'
      }${expenseId}`;

      const AddExpenseFormData = new FormData();

      data.supplierId &&
        operationType !== 'edit' &&
        AddExpenseFormData.append('supplierId', data.supplierId.toString());
      data.cropId &&
        data.expenseType !== 'Supervision' &&
        AddExpenseFormData.append('cropId', data.cropId.toString());
      data.entityId &&
        AddExpenseFormData.append('entityId', data.entityId?.toString());
      data.date &&
        operationType !== 'edit' &&
        AddExpenseFormData.append('date', data.date.toString());
      if (data.deductionType === 1 && expenseType === 'deduction') {
        AddExpenseFormData.append(
          'fractionOfUnits',
          data.fractionOfUnits?.toString() || ''
        );
      } else if (data.bonusType === 1 && expenseType === 'bonus') {
        AddExpenseFormData.append(
          'fractionOfUnits',
          data.fractionOfUnits?.toString() || ''
        );
      } else {
        AddExpenseFormData.append('amount', data.amount?.toString() || '');
      }
      if (operationType === 'edit' && data.expenseType === 'Supervision') {
        AddExpenseFormData.append(
          'type',
          expenseType === 'deduction'
            ? String(data.deductionType)
            : String(data.bonusType)
        );
      }
      data.attachment !== undefined &&
        AddExpenseFormData.append('attachment', data.attachment);
      data.note && AddExpenseFormData.append('note', data.note);

      return axiosClient[requestMethod](url, AddExpenseFormData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          expenseType === 'deduction' ? 'supplierdeduction' : 'supplierbonus',
        ]);
        queryClient.invalidateQueries(['getWorkdaySummary']);
      },
    }
  );
};

/*****************************************Modals**************************************** *
                             edit worker expenses (Deduction,bonus) 
/********************************************************************************* */
// DATA SHOULD BE FORM DATA
export const useEditWorkerExpenses = (
  id: number,
  modalType: IReconciliation.IModalType
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IReconciliation.IAddEditSupplierExpensesSchema) =>
      axiosClient.put(
        `/company/portalInsights/workday/${
          modalType === 'bonus' ? 'workerbonus' : 'workerdeduction'
        }/${id}`,
        data
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          modalType === 'deduction' ? 'supplierdeduction' : 'supplierbonus',
        ]);
        queryClient.invalidateQueries(['getWorkdaySummary']);
      },
    }
  );
};

/*****************************************Modals**************************************** *
                    delete (supplier, worker) expenses (Deduction, bonus) 
/*************************************************************************************** */
// MISSING PARAM DATA
export const useDeleteSupplierOrWorkerExpenses = (
  modalType: IReconciliation.IModalType
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: IReconciliation.IDeleteExpenseSchema) =>
      axiosClient.delete(
        `/company/portalInsights/workday/${
          modalType === 'bonus'
            ? params.expenseType === 'Supervision'
              ? 'workerbonus'
              : 'supplierbonus'
            : params.expenseType === 'Supervision'
            ? 'workerdeduction'
            : 'supplierdeduction'
        }/${params.id}/${params.expenseId}`
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          modalType === 'deduction' ? 'supplierdeduction' : 'supplierbonus',
        ]);
        queryClient.invalidateQueries(['getWorkdaySummary']);
      },
    }
  );
};

/********************************************************************************* *
                            Reconciliation supplier crops
/********************************************************************************* */
export const getWorkdaySupplierCrops = async ({
  entityId,
  ...restParams
}: Partial<IReconciliation.IGetWorkdaySupplierCropsSchema>): Promise<
  IReconciliation.IGetWorkdaySupplierCrops[]
> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/workday/supplier/crops/${entityId}`,
    {
      params: restParams,
    }
  );
  return data;
};
export const useGetWorkdaySupplierCrops = (
  params: Partial<IReconciliation.IGetWorkdaySupplierCropsSchema>
) => {
  return useQuery(
    ['workdaySupplierCrops', params],
    () => getWorkdaySupplierCrops(params),
    {
      enabled: Boolean(
        (params.entityId === 0 ? true : params.entityId) && params.supplierId
      ),
    }
  );
};

/********************************************************************************* *
                            Reconciliation supplier details Table
/********************************************************************************* */
export const getWorkdaySupplierDetails = async ({
  entityId,
  ...restParams
}: Partial<IReconciliation.IGetWorkdaySupplierDetailsSchema>): Promise<
  IReconciliation.IGetWorkdaySupplierDetails[]
> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/workday/supplier/details/${entityId}`,
    {
      params: restParams,
    }
  );
  return data;
};
export const useGetWorkdaySupplierDetails = (
  params: Partial<IReconciliation.IGetWorkdaySupplierDetailsSchema>
) => {
  return useQuery(
    ['workdaySupplierDetails', params],
    () => getWorkdaySupplierDetails(params),
    {
      enabled: Boolean(
        (params.entityId === 0 ? true : params.entityId) && params.supplierId
      ),
    }
  );
};

/*********************************Summary****************************************** *
                            Daily Dues Worker Level
/********************************************************************************* */
export const getWorkerDailyDuesSummary = async ({
  entityId,
  ...restParams
}: IReconciliation.IGetWorkerDailyDuesSummarySchema): Promise<IReconciliation.IGetWorkerDailyDuesSummaryWithDates> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/daily-dues/workers/summary/${entityId}`,
    {
      params: restParams,
    }
  );
  return data;
};
export const useGetWorkerDailyDuesSummary = (
  params: IReconciliation.IGetWorkerDailyDuesSummarySchema
) => {
  return useQuery(
    ['workerDailyDuesSummary', params],
    () => getWorkerDailyDuesSummary(params),
    {
      enabled: !!params.entityId && !!params.endDate && !!params.startDate,
    }
  );
};
/*********************************Day Workers****************************************** *
                            Daily Dues Worker Level
/********************************************************************************* */
export const getWorkerDailyDuesDetails = async ({
  entityId,
  ...restParams
}: IReconciliation.IGetWorkerDailyDuesDetailsSchema): Promise<IReconciliation.IGetWorkersDailyDuesDetails> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/daily-dues/workers/${entityId}`,
    {
      params: restParams,
    }
  );
  return data;
};
export const useGetWorkerDailyDuesDetails = (
  params: IReconciliation.IGetWorkerDailyDuesDetailsSchema
) => {
  return useQuery(
    ['workerDailyDuesDetails', params],
    () => getWorkerDailyDuesDetails(params),
    {
      enabled: !!params.entityId && !!params.date,
    }
  );
};
/*********************************Day Workers****************************************** *
                            Search Worker name
/********************************************************************************* */
const getWorkersList = async (
  params: Omit<
    ISettlements.ISearchWorkerSettlementSchema,
    'endDate' | 'startDate'
  >
) => {
  const { data } = await axiosClient.get<ISettlements.IGetWorkers>(
    `/company/portalInsights/daily-dues/workers`,
    { params }
  );
  return {
    items: data.workers,
    currentPage: params.pageNumber,
    totalPages: Math.ceil(data.totalCount / params.pageSize) ?? 1,
    totalItems: data.totalCount,
    nextPage:
      params?.pageNumber * params?.pageSize < data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};

export const useGetWorkersList = (
  params: Omit<
    ISettlements.ISearchWorkerSettlementSchema,
    'pageSize' | 'pageNumber' | 'endDate' | 'startDate'
  >
) => {
  return useInfiniteQuery({
    queryKey: ['getWorkersList', params],
    queryFn: ({ pageParam }) =>
      getWorkersList({
        ...params,
        pageSize: 10,
        pageNumber: pageParam ?? 1,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,

    enabled: !!params.entityId && !!params.search && params.search.length > 1,
  });
};
